import React, { useMemo, useState } from 'react';
import {
  selectDoctorBuckets,
  selectVisibleRows,
  selectIpdRevenueTerms,
  selectInsuranceSettled,
  selectDischargeCounts,
} from './monthlyReportModel';
import './ByDoctorTable.css';

/**
 * ByDoctorTable (DOCTOR-REPORTS P1, FE-4 — build-plan §2 FROZEN CONTRACT,
 * `ipd.by_doctor`). The "By doctor" tab: every bucket the server returns,
 * in server order (Unassigned always last, even at 0/₹0 — D2), NEVER
 * truncated. Money reads ONLY `selectIpdRevenueTerms` / `selectInsuranceSettled`
 * on the bucket — never a re-sum. The footer total reads `data.ipd` directly
 * (the org block), never a client-side sum of the rows (§2.1's tie-out is
 * enforced server-side; this component just renders both sides of it).
 *
 * - `showPaidAfterDischarge` (D3) hides the "Paid after discharge" column
 *   org-wide, driven by the page the same way as `IpdRevenue`/`DischargeRegister`.
 * - Insurance settled + Concessions sit visually apart from the cash
 *   columns (C1 — a vertical rule + tinted header, never summed in).
 * - Search narrows the VISIBLE rows only (label contains, case-insensitive)
 *   — the footer total is unaffected by search (R10 long-list rule).
 * - Sticky header + sticky footer, horizontal scroll; long doctor names
 *   WRAP onto multiple lines (never truncate/ellipsis — business R1) with a
 *   `title` tooltip; the "N not reconciled" badge sits on its own line
 *   under the name.
 * - Row click selects the doctor (`onSelect(key)`); clicking the already-
 *   selected row clears the filter (`onSelect(null)`) — the same one state
 *   the page shares with the `DoctorPicker`.
 */
export default function ByDoctorTable({
  data, selectedKey = null, onSelect, showPaidAfterDischarge = true,
}) {
  const [query, setQuery] = useState('');
  // Recon item 18: an all-zero Unassigned/Unknown bucket (no discharges, no
  // money, no unreconciled count) is hidden here — the "{n} of {m} doctors"
  // count below rides the same visible list, never the raw bucket count.
  const buckets = useMemo(() => selectVisibleRows(selectDoctorBuckets(data?.ipd)), [data]);
  const orgTerms = selectIpdRevenueTerms(data?.ipd);
  const orgInsurance = selectInsuranceSettled(data?.ipd);
  const orgDischarges = selectDischargeCounts(data?.ipd);

  const q = query.trim().toLowerCase();
  const shownBuckets = q ? buckets.filter((b) => b.label.toLowerCase().includes(q)) : buckets;

  if (!orgTerms) return null;

  return (
    <div data-testid="by-doctor-table" className="bdt rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <input
          type="text"
          data-testid="by-doctor-search"
          placeholder="Search doctor…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="max-w-xs flex-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
        />
        <span data-testid="by-doctor-count" className="text-xs text-slate-500 dark:text-slate-400">
          {shownBuckets.length} of {buckets.length} doctors
        </span>
      </div>

      <div className="bdt-scroll overflow-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="bdt-table w-full text-left text-sm">
          <thead>
            <tr className="bdt-head text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <th className="px-3 py-2">Doctor</th>
              <th className="px-3 py-2 text-right">Discharges</th>
              <th className="px-3 py-2 text-right">Net billed</th>
              <th className="px-3 py-2 text-right">Deposits adjusted</th>
              {showPaidAfterDischarge && <th className="px-3 py-2 text-right">Paid after discharge</th>}
              <th className="px-3 py-2 text-right">Written off</th>
              <th className="px-3 py-2 text-right">Still outstanding</th>
              <th className="bdt-col-noncash px-3 py-2 text-right">Insurance settled (non-cash)</th>
              <th className="bdt-col-noncash px-3 py-2 text-right">Concessions (already deducted)</th>
            </tr>
          </thead>
          <tbody>
            {shownBuckets.map((bucket) => {
              const terms = selectIpdRevenueTerms(bucket);
              const insurance = selectInsuranceSettled(bucket);
              const isSelected = selectedKey === bucket.key;
              return (
                <tr
                  key={bucket.key}
                  data-testid={`by-doctor-row-${bucket.key}`}
                  aria-selected={isSelected}
                  onClick={() => onSelect?.(isSelected ? null : bucket.key)}
                  className={`bdt-row cursor-pointer border-t border-slate-100 dark:border-slate-800 ${
                    isSelected ? 'bdt-row-selected' : ''
                  }`}
                >
                  <td className="px-3 py-2">
                    <div className="bdt-name-cell">
                    <span className="bdt-name-txt" title={bucket.label}>{bucket.label}</span>
                    {bucket.unreconciled > 0 && (
                      <span
                        data-testid={`by-doctor-unreconciled-${bucket.key}`}
                        className="whitespace-nowrap rounded border border-amber-400 bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:border-amber-600 dark:bg-amber-900/40 dark:text-amber-200"
                      >
                        {bucket.unreconciled} not reconciled
                      </span>
                    )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">{bucket.discharges?.total ?? 0}</td>
                  <td className="px-3 py-2 text-right">{terms.netBilled}</td>
                  <td className="px-3 py-2 text-right">{terms.depositsAdjusted}</td>
                  {showPaidAfterDischarge && <td className="px-3 py-2 text-right">{terms.duesCollected}</td>}
                  <td className="px-3 py-2 text-right">{terms.writtenOff}</td>
                  <td className="px-3 py-2 text-right">{terms.stillOutstanding}</td>
                  <td className="bdt-col-noncash px-3 py-2 text-right">{insurance.insuranceSettled}</td>
                  <td className="bdt-col-noncash px-3 py-2 text-right">{terms.concessions}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr data-testid="by-doctor-footer-total" className="bdt-foot font-semibold text-slate-900 dark:text-slate-50">
              <td className="px-3 py-2">Total — organisation</td>
              <td className="px-3 py-2 text-right">{orgDischarges?.total ?? 0}</td>
              <td className="px-3 py-2 text-right">{orgTerms.netBilled}</td>
              <td className="px-3 py-2 text-right">{orgTerms.depositsAdjusted}</td>
              {showPaidAfterDischarge && <td className="px-3 py-2 text-right">{orgTerms.duesCollected}</td>}
              <td className="px-3 py-2 text-right">{orgTerms.writtenOff}</td>
              <td className="px-3 py-2 text-right">{orgTerms.stillOutstanding}</td>
              <td className="bdt-col-noncash px-3 py-2 text-right">{orgInsurance.insuranceSettled}</td>
              <td className="bdt-col-noncash px-3 py-2 text-right">{orgTerms.concessions}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p data-testid="by-doctor-caption" className="mt-3 text-xs text-slate-500 dark:text-slate-400">
        Money is counted under each patient&apos;s primary doctor at discharge. If the doctor was changed
        during the stay, the whole stay counts under the doctor at discharge. Discharges where no primary
        doctor was recorded on the admission are shown as &quot;Unassigned&quot;.
      </p>
    </div>
  );
}
